import React, { useMemo } from 'react';
import { FloorPlan } from '../types';

interface MinimapViewportIndicatorProps {
  viewState: { scale: number; translateX: number; translateY: number };
  viewerDimensions: { width: number; height: number };
  planData: FloorPlan;
  minimapScale: number;
  rotationX: number;
  rotationZ: number;
}

export const MinimapViewportIndicator = ({
  viewState,
  viewerDimensions,
  planData,
  minimapScale,
  rotationX,
  rotationZ,
}: MinimapViewportIndicatorProps) => {

  const points = useMemo(() => {
    // This matrix represents the transformation of the canvas (panning and zooming)
    const canvasMatrix = new DOMMatrix()
      .translate(viewState.translateX*1.2+600, viewState.translateY*0.05)
      .scale(viewState.scale);

    // This matrix represents the 3D rotation applied to the perspective container
    // We achieve rotateX and rotateZ by calling rotate() twice in sequence.
    // rotate(angleX, angleY, angleZ)
    const perspectiveMatrix = new DOMMatrix()
      .rotate(rotationX, 0, 0) // Equivalent to rotateX(rotationX)
      .rotate(0, 0, rotationZ); // Equivalent to rotateZ(rotationZ)
    
    // The final transformation matrix from floor plan to screen
    const transformMatrix = perspectiveMatrix.multiply(canvasMatrix);

    // We need the inverse to go from screen coordinates back to floor plan coordinates
    const inverseMatrix = transformMatrix.inverse();

    // The four corners of our viewport in screen coordinates
    const screenCorners = [
      { x: 0, y: 0 }, // Top-left
      { x: viewerDimensions.width, y: 0 }, // Top-right
      { x: viewerDimensions.width, y: viewerDimensions.height }, // Bottom-right
      { x: 0, y: viewerDimensions.height }, // Bottom-left
    ];

    // Map each screen corner back to its corresponding point on the 2D floor plan
    const floorPlanCorners = screenCorners.map(corner => {
      return inverseMatrix.transformPoint(new DOMPoint(corner.x, corner.y));
    });

    // Convert the floor plan coordinates to minimap coordinates and format for SVG polygon
    return floorPlanCorners
      .map(p => {
        const minimapX = (p.x - planData.dimensions.minX) * minimapScale;
        const minimapY = (p.y - planData.dimensions.minY) * minimapScale;
        return `${minimapX},${minimapY}`;
      })
      .join(' ');
  }, [viewState, viewerDimensions, planData, minimapScale, rotationX, rotationZ]);

  if (!points) return null;

  return (
    <svg className="minimap-svg-overlay">
      <polygon className="minimap-viewport-polygon" points={points} />
    </svg>
  );
};